<template>
  <ContentWrap>
    <el-alert
      class="mb-16px"
      type="info"
      :closable="false"
      title="对齐微宝典「关注及分享」：配置全局分享标题、封面、描述与落地页。标题/封面留空则 C 端回落「商城品牌」名称与 Logo；落地链接留空则进商城首页。商品详情可单独开关分享按钮。"
    />
    <el-row :gutter="24">
      <el-col :span="14" :xs="24">
        <el-form
          ref="formRef"
          v-loading="formLoading"
          :model="formData"
          label-width="140px"
        >
          <el-divider content-position="left">分享设置</el-divider>
          <el-form-item label="分享标题" prop="shareTitle">
            <el-input
              v-model="formData.shareTitle"
              class="!w-420px"
              clearable
              maxlength="128"
              show-word-limit
              placeholder="不填写默认商城名称"
            />
          </el-form-item>
          <el-form-item label="分享封面" prop="shareIcon">
            <UploadImg v-model="formData.shareIcon" height="120px" width="150px" />
            <div class="el-upload__tip" style="line-height: 1.4; margin-top: 6px">
              不选择默认商城 Logo；建议约 <b>5:4</b>（如 500×400）
            </div>
          </el-form-item>
          <el-form-item label="分享描述" prop="shareDesc">
            <el-input
              v-model="formData.shareDesc"
              class="!w-420px"
              type="textarea"
              :rows="3"
              maxlength="256"
              show-word-limit
              placeholder="选填；主要用于 H5/海报"
            />
          </el-form-item>
          <el-form-item label="分享链接" prop="landPath">
            <AppLinkInput v-model="formData.landPath" class="!w-420px" />
            <el-text class="w-full" size="small" type="info">
              用户分享出去的链接，默认为商城首页；可点「选择链接」挑选分类、DIY 页、商品等
            </el-text>
          </el-form-item>

          <el-divider content-position="left">商品详情分享</el-divider>
          <el-form-item label="商品详情分享" prop="goodsDetailShareEnabled">
            <el-radio-group v-model="formData.goodsDetailShareEnabled">
              <el-radio :value="true">开启</el-radio>
              <el-radio :value="false">关闭</el-radio>
            </el-radio-group>
            <el-text class="w-full" size="small" type="info">关闭后商品详情不展示分享入口</el-text>
          </el-form-item>
          <el-form-item label="分享引导文案" prop="goodsDetailShareText">
            <el-input
              v-model="formData.goodsDetailShareText"
              class="!w-420px"
              type="textarea"
              :rows="2"
              maxlength="256"
              show-word-limit
              placeholder="请点击右上角，通过【发送给朋友】邀请好友购买"
            />
          </el-form-item>

          <el-form-item>
            <el-button
              v-hasPermi="['trade:mall-share-config:save']"
              :loading="formLoading"
              type="primary"
              @click="submitForm"
            >
              保 存
            </el-button>
          </el-form-item>
        </el-form>
      </el-col>
      <el-col :span="10" :xs="24">
        <div class="share-preview">
          <div class="share-preview__label">卡片预览</div>
          <div class="share-preview__card">
            <div class="share-preview__mp">紫金山云谷</div>
            <div class="share-preview__title">
              {{ formData.shareTitle || '（空则显示商城品牌名）' }}
            </div>
            <el-image
              v-if="formData.shareIcon"
              class="share-preview__image"
              :src="formData.shareIcon"
              fit="cover"
            />
            <div v-else class="share-preview__image share-preview__image--empty">封面（空则品牌 Logo）</div>
            <div class="share-preview__foot">小程序</div>
          </div>
          <div class="share-preview__land">
            落地：{{ formData.landPath || '/pages/modules/mall/index/index（默认）' }}
          </div>
        </div>
      </el-col>
    </el-row>
  </ContentWrap>
</template>

<script lang="ts" setup>
import * as MallShareConfigApi from '@/api/mall/trade/mallShareConfig'
import { UploadImg } from '@/components/UploadFile'
import AppLinkInput from '@/components/AppLinkInput/index.vue'

defineOptions({ name: 'TradeMallShareConfig' })

const message = useMessage()
const formLoading = ref(false)
const formRef = ref()
const formData = ref<MallShareConfigApi.MallShareConfigVO>({
  shareTitle: '',
  shareIcon: '',
  shareDesc: '',
  landPath: '',
  goodsDetailShareEnabled: true,
  goodsDetailShareText: '请点击右上角，通过【发送给朋友】邀请好友购买'
})

const getForm = async () => {
  formLoading.value = true
  try {
    const data = await MallShareConfigApi.getMallShareConfig()
    formData.value = {
      id: data?.id,
      shareTitle: data?.shareTitle || '',
      shareIcon: data?.shareIcon || '',
      shareDesc: data?.shareDesc || '',
      landPath: data?.landPath || '',
      goodsDetailShareEnabled:
        data?.goodsDetailShareEnabled === undefined || data?.goodsDetailShareEnabled === null
          ? true
          : data.goodsDetailShareEnabled,
      goodsDetailShareText:
        data?.goodsDetailShareText ||
        '请点击右上角，通过【发送给朋友】邀请好友购买'
    }
  } finally {
    formLoading.value = false
  }
}

const submitForm = async () => {
  formLoading.value = true
  try {
    await MallShareConfigApi.saveMallShareConfig({
      shareTitle: formData.value.shareTitle || '',
      shareIcon: formData.value.shareIcon || '',
      shareDesc: formData.value.shareDesc || '',
      landPath: formData.value.landPath || '',
      goodsDetailShareEnabled: formData.value.goodsDetailShareEnabled !== false,
      goodsDetailShareText: formData.value.goodsDetailShareText || ''
    })
    message.success('保存成功')
    await getForm()
  } finally {
    formLoading.value = false
  }
}

onMounted(() => {
  getForm()
})
</script>

<style scoped>
.share-preview__label {
  margin-bottom: 12px;
  font-weight: 600;
  color: #303133;
}
.share-preview__card {
  max-width: 320px;
  padding: 12px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}
.share-preview__mp {
  margin-bottom: 8px;
  color: #909399;
  font-size: 12px;
}
.share-preview__title {
  margin-bottom: 10px;
  color: #303133;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  word-break: break-all;
}
.share-preview__image {
  display: block;
  width: 100%;
  height: 180px;
  border-radius: 4px;
  background: #f5f7fa;
}
.share-preview__image--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 13px;
}
.share-preview__foot {
  margin-top: 10px;
  color: #909399;
  font-size: 12px;
}
.share-preview__land {
  margin-top: 12px;
  max-width: 320px;
  color: #606266;
  font-size: 12px;
  word-break: break-all;
}
</style>
